import React, { FC, useEffect, useState } from "react"
import clsx from "clsx"
import { FaChevronDown } from "react-icons/fa"

import { DetailCard } from "./DetailCard"
import { FlexBox, LoadingBox } from "@components/wrapper"
import { RegularButton } from "@components/button"
import { align } from "@constants/styles"
import CourseDetailServices from "@services/course-detail.services"
import { Review, ReviewSummaries } from "@interfaces/course/course-detail"
import { Avatar, ProgressBar } from "@components/misc"
import { StarRating } from "@components/maker"
import { CourseHeading } from "@modules/course-detail/components/index"

interface CourseReviewProps {
  reviewInfo: ReviewSummaries
  courseId: string
}

export const CourseReview: FC<CourseReviewProps> = ({ reviewInfo, courseId }) => {
  const [isFetching, setIsFetching] = useState(true)

  const [reviews, setReviews] = useState<Review[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalData, setTotalData] = useState(1)

  const [hasPagination, setHasPagination] = useState(false)
  const [isFetchingNext, setIsFetchingNext] = useState(false)

  const course = new CourseDetailServices()

  useEffect(() => {
    const getData = async () => {
      try {
        const res = await course.getReviews(courseId)

        if (res.isSuccess) {
          const result = res.getValue()

          setReviews(result.data)
          setCurrentPage(result.current_page)
          setTotalData(result.total)

          if (result.total >= 10) {
            setHasPagination(true)
          }
        }

        setIsFetching(false)
      } catch (e) {
        setIsFetching(false)
        console.log(e)
      }
    }

    if (courseId) {
      getData()
    }
  }, [courseId])

  async function handleNextPage() {
    setIsFetchingNext(true)

    try {
      const currentReviews = [...reviews]
      const res = await course.getReviews(courseId, currentPage + 1)

      if (res.isSuccess) {
        const result = res.getValue()
        const newReviews = [...currentReviews, ...result.data]

        setReviews(newReviews)
        setCurrentPage(result.current_page)

        if (newReviews.length === totalData) {
          setHasPagination(false)
        }
      }

      setIsFetchingNext(false)
    } catch (e) {
      setIsFetchingNext(false)
    }
  }

  return (
    <DetailCard padding="sm:px-[30px] sm:py-6 px-2.5 py-3" className="sm:mb-10 mb-6">
      <CourseHeading className="sm:mb-5 mb-2">Rating dan Review</CourseHeading>

      {reviewInfo.average_review_rate > 0 ? (
        <div className="sm:flex mb-10">
          <FlexBox
            justify="center"
            align="center"
            className={clsx(
              "bg-green text-white sm:w-[8vw] sm:h-[8vw] font-semibold text-4xl rounded-[10px] sm:mr-[30px] sm:mb-0",
              "w-full mr-0 mb-6 h-[15vh]"
            )}
          >
            {reviewInfo.average_review_rate}
          </FlexBox>
          <div>
            <FlexBox align="center" className="sm:mb-4 mb-2.5">
              <ProgressBar
                progress={reviewInfo.review_rate_five_star}
                width="sm:w-[30vw] w-3/4"
                className="sm:mr-10 mr-3"
                height="sm:h-3 h-2"
                color="bg-yellow"
                bgColor="bg-grey-ec"
              />
              <StarRating value={5} />
            </FlexBox>
            <FlexBox align="center" className="sm:mb-4 mb-2.5">
              <ProgressBar
                progress={reviewInfo.review_rate_four_star}
                width="sm:w-[30vw] w-3/4"
                className="sm:mr-10 mr-3"
                height="sm:h-3 h-2"
                color="bg-yellow"
                bgColor="bg-grey-ec"
              />
              <StarRating value={4} />
            </FlexBox>
            <FlexBox align="center" className="sm:mb-4 mb-2.5">
              <ProgressBar
                progress={reviewInfo.review_rate_three_star}
                width="sm:w-[30vw] w-3/4"
                className="sm:mr-10 mr-3"
                height="sm:h-3 h-2"
                color="bg-yellow"
                bgColor="bg-grey-ec"
              />
              <StarRating value={3} />
            </FlexBox>
            <FlexBox align="center" className="sm:mb-4 mb-2.5">
              <ProgressBar
                progress={reviewInfo.review_rate_two_star}
                width="sm:w-[30vw] w-3/4"
                className="sm:mr-10 mr-3"
                height="sm:h-3 h-2"
                color="bg-yellow"
                bgColor="bg-grey-ec"
              />
              <StarRating value={2} />
            </FlexBox>
            <FlexBox align="center">
              <ProgressBar
                progress={reviewInfo.review_rate_one_star}
                width="sm:w-[30vw] w-3/4"
                className="sm:mr-10 mr-3"
                height="sm:h-3 h-2"
                color="bg-yellow"
                bgColor="bg-grey-ec"
              />
              <StarRating value={1} />
            </FlexBox>
          </div>
        </div>
      ) : (
        <FlexBox align="center" justify="center" className="h-[30vh]">
          Belum ada review di kelas ini
        </FlexBox>
      )}

      {isFetching && <LoadingBox height="h-[30vh]" />}

      {reviews.length ? (
        <>
          {reviews.map((value, index) => (
            <div
              className={clsx(
                "bg-white radius-[10px] sm:py-[30px] sm:px-9 mb-[30px] rounded-[10px]",
                "px-[15px] py-5"
              )}
              key={index}
            >
              <FlexBox align="center" className="mb-3">
                <StarRating value={value.review_rate} className="mr-4" />
                <div className="text-sm">{value.review_time_ago}</div>
              </FlexBox>

              <div className="mb-3 sm:text-base text-sm">{value.review_description}</div>

              <FlexBox align="center">
                <Avatar
                  src={value.user.user_picture}
                  alt="reviewer"
                  size={60}
                  className={clsx("sm:wh-15 mr-6", "wh-10")}
                />
                <div className="text-green font-semibold sm:">{value.user.user_full_name}</div>
              </FlexBox>
            </div>
          ))}

          {hasPagination ? (
            <RegularButton
              variant="transparent"
              className="m-auto py-3 px-7"
              textClassName={align.center}
              isSubmitting={isFetchingNext}
              onClick={handleNextPage}
            >
              <span className="mr-1">Lihat Lebih </span>
              Banyak <FaChevronDown className="ml-1" />
            </RegularButton>
          ) : null}
        </>
      ) : null}
    </DetailCard>
  )
}
